/**
 * Anchor + grid contract for a Hired Equipment submission.
 *
 * The chooser (HE-F02–F05) locks one dimension for the rest of the submission's
 * lifecycle. The grid reads this payload to know which per-row selectors stay
 * open vs. which are inherited from the lock.
 */

export const HIRED_EQUIPMENT_ENTRY_METHODS = ['purchase_order', 'subcontractor', 'task'] as const;

export type HiredEquipmentEntryMethod = (typeof HIRED_EQUIPMENT_ENTRY_METHODS)[number];

export type FuelVehicleTypeKey = 'ON_ROAD' | 'OFF_ROAD';

/** Dated fuels-table row used to resolve $/hr on the hired-equipment form. */
export interface FuelSurchargeLookupRow {
  id: number;
  vehicleType: FuelVehicleTypeKey;
  surchargePerHour: number;
  effectiveAt: string;
}

/**
 * Immutable once the user clicks "Continue to grid".
 *
 * Locked ids are omitted (not null) when that dimension is *not* the anchor so
 * the grid can tell "never locked" from "locked to an empty fallback".
 */
export interface HiredEquipmentAnchor {
  entryMethod: HiredEquipmentEntryMethod;
  lockedVendorId?: number;
  lockedVendorName?: string;
  lockedPoId?: number;
  lockedPoNumber?: string;
  /** PO header description (Module A), shown alongside the number in the anchor summary. */
  lockedPoDescription?: string | null;
  lockedTaskId?: number;
  lockedTaskLabel?: string;
  /** Optional override; WRAP infers from the signed-in user when omitted. */
  serviceAreaId?: number | null;
  /**
   * PO mode only. When true the grid seeds rows from the PO's committed
   * resources; task is then set per line from each resource's own task.
   */
  prePopulate?: boolean;
}

/**
 * Per-row selector policy derived from {@link HiredEquipmentAnchor.entryMethod}.
 * The grid should render plain text (not a dropdown) for any locked column.
 */
export interface HiredEquipmentGridRowLocks {
  vendorLocked: boolean;
  poLocked: boolean;
  taskLocked: boolean;
  rowVendorSelectable: boolean;
  rowPoSelectable: boolean;
  rowTaskSelectable: boolean;
}

/** Unit shown (and persisted) on a lump sum line. */
export const LUMP_SUM_UNIT = '$';
/** Resource label for a lump sum line — the resource *is* "lump sum". */
export const LUMP_SUM_RESOURCE_LABEL = 'Lump Sum';

/**
 * Task types whose STANDING tasks are kept out of every hired-equipment task picker. A standing
 * task is a template meant to be cloned per occurrence, so billing hired equipment against one is
 * always a mistake. Overhead is deliberately absent — its `A*` tasks stay pickable.
 * Mirrors `HE_HIDDEN_STANDING_TYPES` on the server.
 */
export const HE_EXCLUDED_STANDING_TYPES = ['Routine', 'Quantified'];

/** Client-side grid line. Submitted via POST /api/hired-equipment/submissions. */
export interface HiredEquipmentGridRow {
  clientId: string;
  /** ISO `yyyy-mm-dd`. */
  date: string;
  vendorId?: number | null;
  vendorName?: string | null;
  poId?: number | null;
  poNumber?: string | null;
  taskId?: number | null;
  taskLabel?: string | null;
  resourceId?: number | null;
  resourceLabel?: string | null;
  qty: number | null;
  unit: string;
  rate: number | null;
  vendorMaterial: boolean;
  /** Notes shown when Int. Mat. is checked; persisted as Comments 5. */
  vendorMaterialNotes?: string;
  /**
   * HE-F17. `null` = vendor default not configured (do not treat as ineligible).
   * Explicit true/false after an ops user sets the flag.
   */
  fuelSurchargeEligible: boolean | null;
  /**
   * Per-line fuel surcharge $/hr override, for this line only.
   * `null` uses the fuels-table $/hr. Does not change Fuel Management or the resource.
   */
  fuelSurchargeOverridePerHour?: number | null;
  /**
   * Per-line OT application. Defaults on when the catalogue resource is
   * overtime-eligible and the unit is Hours. User can uncheck.
   */
  overtimeEligible: boolean;
  /** Catalogue `VendorResource.overtimeEligible`. Gates the checkbox. */
  resourceOvertimeEligible?: boolean | null;
  /** From VendorResource.vehicleType — On-Road / Off-Road / N/A; drives fuels-table lookup. */
  vehicleType?: string | null;
  /** Optional per-line file stored in B2/S3. Not required to submit. */
  attachment?: HiredEquipmentRowAttachment | null;
  /**
   * HE-F20 optional paper ticket. Free text, not required. Distinct from the
   * WRAP-generated reference on the confirmation screen (e.g. HE1626-00001).
   */
  ticketNumber?: string | null;
  /**
   * TrueContext / WRAP task section id (e.g. `1.0`, `1.1`) used to group form
   * attachments under `WorkCompleted.Tasks 1.N`.
   */
  taskGroupId?: string;
  /**
   * Set by {@link duplicateRow} on the copy it produces; cleared by the grid's own
   * `patch` the moment any field on the row changes. Purely a display hint so an
   * unreviewed duplicate reads differently from a line someone has actually looked
   * at — never sent to the server.
   */
  duplicatedPending?: boolean;
  /**
   * Flat-amount line: no resource, no qty, no rate, no fuel, never overtime.
   * Stored as qty 1 x rate = amount with unit `$` so every existing amount,
   * total, PO-balance and effort computation works on it unchanged; this flag
   * is the only thing that says "the rate cell is really an amount".
   */
  lumpSum: boolean;
  /**
   * This row's task was created or cloned during this entry session, so the submission owns it.
   * Sent with the submit payload: the server stamps `Task.createdFromFormId` on these, which is
   * what tells approval which tasks it may close. Cleared whenever the row's task is swapped.
   */
  taskCreatedInSession?: boolean;
}

export interface HiredEquipmentGridProps {
  anchor: HiredEquipmentAnchor;
  rows: HiredEquipmentGridRow[];
  onRowsChange: (rows: HiredEquipmentGridRow[]) => void;
  vendors: VendorOption[];
  taskOptionForRow: (row: HiredEquipmentGridRow) => TaskOption | null;
  searchTasks: (query: string) => Promise<TaskOption[]>;
  purchaseOrders: PurchaseOrderOption[];
  /**
   * Tasks each PO carries (HE-F13). Missing key = not loaded yet (fail open
   * when filtering POs). Empty array = loaded, PO carries no tasks.
   */
  tasksByPoId?: Record<number, TaskOption[]>;
  resourcesForRow: (row: HiredEquipmentGridRow) => ResourceOption[];
  /** Dated On-Road / Off-Road rates from the fuels table. */
  fuelSurcharges?: FuelSurchargeLookupRow[];
  attemptedSubmit?: boolean;
  onAddLine: () => void;
  /** Omit to hide the lump-sum action (e.g. read-only callers). */
  onAddLumpSumLine?: () => void;
  onDuplicateLine: (clientId: string) => void;
  onRemoveLine: (clientId: string) => void;
  onReAddPoResources?: () => void;
  /** Approval detailed view: disable edits and hide add/duplicate/delete. */
  readOnly?: boolean;
  /**
   * Inline task creation from a row's task picker. Omit when the user cannot
   * create tasks - the menu action is then hidden.
   */
  onCreateTask?: (row: HiredEquipmentGridRow) => void;
  /** Open the selected task's detail modal from a row. */
  onViewTask?: (task: TaskOption, row: HiredEquipmentGridRow) => void;
  /**
   * Rows whose duplicated task is still being cloned. Their Duplicate action is disabled so a
   * second click cannot spawn another task while the first is in flight.
   */
  cloningClientIds?: Set<string>;
}

export interface VendorOption {
  id: number;
  name: string;
  /**
   * From vendor/subcontractor setup when that column exists (HE-F17).
   * Omit when not configured — never invent `false`.
   */
  fuelSurchargeEligible?: boolean;
}

export interface TaskOption {
  id: number;
  description: string;
  name?: string;
  taskNumber?: string;
  /** Present when the task belongs to a project. */
  projectName?: string | null;
  /** `Task.type` — 'Routine', 'Quantified', 'Overhead', … Absent on older cached options. */
  type?: string | null;
  /** `Task.mode` — 'specific' or 'standing'. Null/absent means specific (the schema default). */
  mode?: string | null;
}

export interface PurchaseOrderOption {
  id: number;
  poNumber: string;
  vendorId: number;
  /** Header description from Module A, shown in the HE PO picker. */
  description?: string | null;
}

/** PO picker row for the HE chooser — mapped from Module A `PurchaseOrder`. */
export interface HiredEquipmentPurchaseOrder extends PurchaseOrderOption {
  status: string;
}

/**
 * Prefill/mapping DTO from a PO line item onto a grid row.
 * `id` is the line-item id; `equipmentMasterId` is the VendorResource id when present.
 */
export interface HiredEquipmentPoResource {
  id: number;
  poId: number;
  description: string;
  equipmentMasterId?: number | null;
  rate?: number | null;
  unitOfMeasurement?: string | null;
  qty?: number | null;
  taskId?: number | null;
  taskNumber?: string | null;
  taskDescription?: string | null;
  taskLabel?: string | null;
  vehicleType?: string | null;
  overtimeEligible?: boolean | null;
}

export interface ResourceOption {
  id: number;
  label: string;
  rate?: number | null;
  unit?: string | null;
  taskId?: number | null;
  taskLabel?: string | null;
  vehicleType?: string | null;
  overtimeEligible?: boolean;
  /** True when this option is a line on the selected PO. */
  onPurchaseOrder?: boolean;
  /** VendorResource id when `id` is a PO line-item id. */
  catalogueId?: number | null;
}

export type HiredEquipmentSessionStatus = 'pending' | 'approved' | 'need_revisions' | 'declined';

export interface HiredEquipmentRowAttachment {
  filename: string;
  contentType: string;
  size: number;
  url: string;
  key?: string;
}

export interface HiredEquipmentSessionSubmission {
  /** Quotable reference number (HE-F23), e.g. HE1626-00001. */
  id: string;
  createdAt: string;
  anchor: HiredEquipmentAnchor;
  rows: HiredEquipmentGridRow[];
  lineCount: number;
  totalValue: number;
  status: HiredEquipmentSessionStatus;
  /**
   * Approvals form id after WRAP persist. Used to open the real Approvals card.
   */
  formId?: string;
}

/** HE-F20 paper ticket FormField name (optional grid text). */
export const HIRED_EQUIPMENT_RESERVED_TICKET_FIELD = 'TicketNumber';
